import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/lib/auth";
import { avatarFor } from "@/data/demo";
import { Card, Btn, Icon } from "@/components/kit";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STAT_DEFS = [
  { k: "days_active", label: "days active", icon: "CalendarCheck" },
  { k: "contributions", label: "contributions", icon: "MessageSquare" },
  { k: "posts", label: "posts", icon: "PenSquare" },
  { k: "replies", label: "replies", icon: "CornerDownRight" },
  { k: "content_read", label: "reads", icon: "BookOpen" },
  { k: "saved", label: "saved", icon: "Bookmark" },
  { k: "likes_received", label: "likes received", icon: "Heart" },
  { k: "followers", label: "followers", icon: "Users" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

export default function Profile() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("summary");
  const [summary, setSummary] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: user?.name || "", about: user?.about || "", tagline: user?.tagline || "",
    arn: user?.arn || "", location: user?.location || "", website: user?.website || "",
    timezone: user?.timezone || "Asia/Kolkata",
  });

  useEffect(() => {
    setForm({
      name: user?.name || "", about: user?.about || "", tagline: user?.tagline || "",
      arn: user?.arn || "", location: user?.location || "", website: user?.website || "",
      timezone: user?.timezone || "Asia/Kolkata",
    });
  }, [user]);

  useEffect(() => {
    api.get("/activity/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const avatarUrl = useMemo(
    () => (user?.avatar_path ? `${API}/profile/avatar/${user.id}?v=${user.avatar_v || 0}` : avatarFor(user?.name)),
    [user]
  );
  const username = (user?.name || "User").replace(/\s+/g, "_");

  const onPick = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser(data);
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const stats = summary?.stats || {};

  return (
    <div className="fade-up max-w-4xl mx-auto">
      {/* Header */}
      <Card className="overflow-hidden !p-0">
        <div className="h-24 grad-violet-coral" />
        <div className="px-5 sm:px-7 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12">
            <div className="relative shrink-0">
              <img src={avatarUrl} alt={user?.name} data-testid="profile-avatar-img"
                className="w-24 h-24 rounded-full object-cover ring-4 ring-white bg-white" />
              <button onClick={onPick} data-testid="avatar-upload-btn" title="Change photo"
                className="absolute bottom-0 right-0 w-8 h-8 rounded-full grad-amber text-black grid place-items-center shadow-md hover:brightness-105">
                {uploading ? <Icon name="Loader" size={15} className="animate-spin" /> : <Icon name="Camera" size={15} />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="avatar-upload-input" />
            </div>
            <div className="min-w-0 flex-1 sm:pb-1">
              <h1 className="font-head font-bold text-2xl text-gray-900 flex items-center gap-1.5 truncate">
                {user?.name} <Icon name="BadgeCheck" size={18} className="text-blue-500 shrink-0" />
              </h1>
              <p className="text-sm text-gray-400">@{username}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 mt-5 text-sm text-gray-500">
            <span><span className="text-gray-400">Joined</span> <b className="text-gray-700 font-medium">{fmtDate(user?.created_at)}</b></span>
            <span><span className="text-gray-400">Role</span> <b className="text-gray-700 font-medium capitalize">{user?.role || "MFD"}</b></span>
            {user?.arn && <span><span className="text-gray-400">ARN</span> <b className="text-gray-700 font-medium">{user.arn}</b></span>}
            {user?.location && <span className="flex items-center gap-1"><Icon name="MapPin" size={13} className="text-gray-400" /> {user.location}</span>}
            <span className="flex items-center gap-1"><Icon name="Mail" size={13} className="text-gray-400" /> {user?.email}</span>
          </div>
          {user?.tagline && <p className="mt-3 text-gray-600 text-sm italic">“{user.tagline}”</p>}
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 mt-5 border-b border-gray-200">
        {[["summary", "Summary", "LayoutDashboard"], ["edit", "Edit Profile", "Settings"]].map(([k, l, ic]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`profile-tab-${k}`}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-blue-500 text-blue-600" : "border-transparent text-gray-400 hover:text-gray-700"}`}>
            <Icon name={ic} size={15} /> {l}
          </button>
        ))}
      </div>

      {tab === "summary" && (
        <div className="mt-5 space-y-5" data-testid="profile-summary">
          <Card>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-4">Stats</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {STAT_DEFS.map((s) => (
                <div key={s.k} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gray-50 grid place-items-center shrink-0"><Icon name={s.icon} size={16} className="text-gray-400" /></div>
                  <div>
                    <div className="font-head font-bold text-lg leading-none">{stats[s.k] ?? 0}</div>
                    <div className="text-[11px] text-gray-400 mt-0.5">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <h3 className="font-head font-semibold mb-2 flex items-center gap-2"><Icon name="UserCircle" size={18} className="text-violet-500" /> About me</h3>
            {user?.about
              ? <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">{user.about}</p>
              : <p className="text-sm text-gray-400">No bio yet. <button onClick={() => setTab("edit")} className="text-blue-600 font-medium">Add one →</button></p>}
            {user?.website && (
              <a href={user.website.startsWith("http") ? user.website : `https://${user.website}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 font-medium mt-3 hover:underline">
                <Icon name="Globe" size={14} /> {user.website}
              </a>
            )}
          </Card>

          {summary?.journey && (
            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide font-bold mb-1">Journey</div>
                  <div className="font-head font-bold text-lg flex items-center gap-2"><Icon name="Trophy" size={18} className="text-amber-500" /> {summary.journey.current}</div>
                  <p className="text-sm text-gray-500 mt-1">{summary.journey.next_hint}</p>
                </div>
                <Btn variant="soft" onClick={() => nav("/app/my-mfdx")} data-testid="profile-view-journey"><Icon name="ArrowRight" size={15} /> Full journey</Btn>
              </div>
            </Card>
          )}

          {summary?.insights?.length > 0 && (
            <Card>
              <h3 className="font-head font-semibold mb-3 flex items-center gap-2"><Icon name="Sparkles" size={18} className="text-blue-500" /> Insights</h3>
              <ul className="space-y-2">
                {summary.insights.map((t, i) => (
                  <li key={`${i}-${t.slice(0, 16)}`} className="text-sm text-gray-600 flex gap-2"><Icon name="TrendingUp" size={14} className="text-amber-500 shrink-0 mt-0.5" /> {t}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}

      {tab === "edit" && (
        <div className="mt-5" data-testid="profile-edit">
          <Card>
            <div className="flex items-center gap-4 pb-5 mb-5 border-b border-gray-100">
              <img src={avatarUrl} alt="avatar" className="w-16 h-16 rounded-full object-cover" />
              <div>
                <Btn variant="soft" onClick={onPick} data-testid="avatar-change-btn" disabled={uploading}>
                  {uploading ? <><Icon name="Loader" size={15} className="animate-spin" /> Uploading…</> : <><Icon name="Upload" size={15} /> Change photo</>}
                </Btn>
                <p className="text-[11px] text-gray-400 mt-1.5">JPG, PNG or WebP · up to 5 MB</p>
              </div>
            </div>

            <div className="space-y-4">
              <Field label="Display name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" className={INP} /></Field>
              <Field label="Tagline" hint="A short one-liner shown on your profile">
                <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="e.g. Helping Pune families invest with confidence" data-testid="profile-tagline-input" className={INP} />
              </Field>
              <Field label="About me">
                <textarea value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} rows={4} placeholder="Tell the community about yourself, your experience and how you help clients…" data-testid="profile-about-input" className={`${INP} resize-none`} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="ARN"><input value={form.arn} onChange={(e) => setForm({ ...form, arn: e.target.value })} placeholder="ARN-123456" data-testid="profile-arn-input" className={INP} /></Field>
                <Field label="Location"><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Pune, Maharashtra" data-testid="profile-location-input" className={INP} /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Website"><input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="yoursite.com" data-testid="profile-website-input" className={INP} /></Field>
                <Field label="Timezone">
                  <select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} data-testid="profile-timezone-input" className={INP}>
                    {["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "America/New_York", "UTC"].map((tz) => <option key={tz}>{tz}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <Btn onClick={save} disabled={saving} data-testid="profile-save-btn">
                {saving ? <><Icon name="Loader" size={15} className="animate-spin" /> Saving…</> : <><Icon name="Check" size={16} /> Save changes</>}
              </Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

const INP = "w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm outline-none focus:border-blue-400 bg-white";

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}{hint && <span className="font-normal text-gray-400 text-xs ml-2">{hint}</span>}</label>
    {children}
  </div>
);
